import { useQuery } from '@tanstack/react-query';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { Tenant } from '../../../schemas/tenant.schema';

export const useTenant = (organizationId: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: ['tenant', organizationId],
    queryFn: async () => {
      if (!organizationId) throw new Error('Organization ID is required');

      const tenantRef = doc(db, 'tenants', organizationId);
      const tenantDoc = await getDoc(tenantRef);

      if (!tenantDoc.exists()) {
        return null;
      }

      // Get subscribed cities and MLS from subcollections
      const [subscribedCitiesSnapshot, subscribedMLSSnapshot] = await Promise.all([
        getDocs(collection(db, 'tenants', organizationId, 'subscribedCities')),
        getDocs(collection(db, 'tenants', organizationId, 'subscribedMLS')),
      ]);

      const subscribedCities = subscribedCitiesSnapshot.docs.map((doc) => doc.id);

      // Get full MLS data including agentId and officeId arrays
      const subscribedMLS = subscribedMLSSnapshot.docs.map((doc) => ({
        boardCode: doc.id,
        ...doc.data(),
      }));

      const tenantData = {
        id: tenantDoc.id,
        ...tenantDoc.data(),
      } as Tenant;

      // Add subcollection data
      tenantData.cities = subscribedCities;
      tenantData.subscribedMLS = subscribedMLS.map((mls) => mls.boardCode); // Keep backward compatibility
      tenantData.subscribedMLSDetails = subscribedMLS; // Add full MLS details

      return tenantData;
    },
    enabled: enabled && !!organizationId,
  });
};
